// Wrapper around a wallet extension signing call that retries once if the
// first attempt errors AND the browser window lost/regained focus in the
// meantime. The retry exists for the password-unlock case: some wallets
// reject the initial signPsbt if the user hasn't unlocked the extension,
// then succeed after the user unlocks and focus returns.
//
// Critical invariant: retry ONLY fires if attempt 1 actually errored.
// Multi-popup providers (e.g. Unisat's signPurchase, which signs setup
// then purchase sequentially inside ONE request call) briefly surrender
// focus between popups. Without gating on an error, that mid-call focus
// return would fire a spurious retry and re-run the whole request — the
// user would be re-prompted to sign the first PSBT again. See
// wallet/unisat.tsx:signPurchase for the multi-popup shape this guards.

import { isUserRejection } from './signing-errors'

const SIGN_RETRY_GRACE_MS = 1500
const SIGN_RETRY_SETTLE_MS = 100

export function runExternalSigningRequest<T>(
	label: string,
	request: () => Promise<T>,
	graceMs: number = SIGN_RETRY_GRACE_MS
): Promise<T> {
	if (typeof window === 'undefined') {
		return request()
	}

	return new Promise<T>((resolve, reject) => {
		let settled = false
		let retried = false
		let firstError: any = null
		let graceTimer: ReturnType<typeof setTimeout> | null = null
		let retryTimer: ReturnType<typeof setTimeout> | null = null
		let sawWalletContextSwitch = false
		let sawWalletContextReturn = false

		const cleanup = () => {
			window.removeEventListener('blur', handleBlur)
			window.removeEventListener('focus', handleContextReturn)
			window.removeEventListener('pageshow', handleContextReturn)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			if (graceTimer) {
				clearTimeout(graceTimer)
				graceTimer = null
			}
			if (retryTimer) {
				clearTimeout(retryTimer)
				retryTimer = null
			}
		}

		const finishResolve = (value: T) => {
			if (settled) return
			settled = true
			cleanup()
			resolve(value)
		}

		const finishReject = (error: any) => {
			if (settled) return
			settled = true
			cleanup()
			reject(error)
		}

		const startAttempt = (attempt: number) => {
			request()
				.then((value) => {
					finishResolve(value)
				})
				.catch((error) => {
					if (settled) return
					if (isUserRejection(error)) {
						finishReject(error)
						return
					}

					if (attempt === 1 && !retried) {
						firstError = error
						// Focus can return before a rejected provider promise reaches the
						// page. Recover from that lost-event race too.
						maybeRetry()
						if (!graceTimer) {
							graceTimer = setTimeout(() => {
								graceTimer = null
								if (!retried) {
									finishReject(firstError)
								}
							}, graceMs)
						}
						return
					}

					finishReject(error || firstError)
				})
		}

		const maybeRetry = () => {
			if (
				settled ||
				retried ||
				retryTimer ||
				!sawWalletContextSwitch ||
				!sawWalletContextReturn
			)
				return
			// Only retry when attempt 1 actually errored. Without this, any
			// focus return during a healthy multi-popup sequence triggers a
			// second full invocation of `request()`.
			if (!firstError) return

			// Let the extension release its previous approval lock before opening
			// another request. A synchronous retry can itself be rejected as pending.
			retryTimer = setTimeout(() => {
				retryTimer = null
				if (settled || retried) return
				retried = true
				if (graceTimer) {
					clearTimeout(graceTimer)
					graceTimer = null
				}

				console.log(`[wallet] retrying ${label} after focus returned`)
				startAttempt(2)
			}, SIGN_RETRY_SETTLE_MS)
		}

		const handleContextReturn = () => {
			if (!sawWalletContextSwitch) return
			sawWalletContextReturn = true
			maybeRetry()
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				sawWalletContextSwitch = true
				sawWalletContextReturn = false
				return
			}

			if (document.visibilityState === 'visible') {
				handleContextReturn()
			}
		}

		const handleBlur = () => {
			sawWalletContextSwitch = true
			sawWalletContextReturn = false
		}

		window.addEventListener('blur', handleBlur)
		window.addEventListener('focus', handleContextReturn)
		window.addEventListener('pageshow', handleContextReturn)
		document.addEventListener('visibilitychange', handleVisibilityChange)

		startAttempt(1)
	})
}
